/**
 * MoveCommentModal (S1.1)
 *
 * Every taxonomy move (drag reparent OR same-parent reorder) is audited with
 * a MANDATORY change comment (S0.4 contract) — this small modal collects it
 * after a drop passes the client-side guards, then commits the mutation.
 */

import { useEffect, useMemo, useState } from 'react';
import { App, Input, Modal, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import type { TaxonomyMoveIntent } from '@/components/common/TaxonomyTreeEditor';
import { buildBreadcrumb, extractGuardError, useTaxonomyMutations } from '@/hooks/useTaxonomy';
import type { ProductCategory } from '@/types/api/productCategory';

const { Text } = Typography;

export interface MoveCommentModalProps {
  /** The pending move (modal renders only while set). */
  intent: TaxonomyMoveIntent | null;
  nodes: ProductCategory[];
  onClose: () => void;
  /** Called after the move committed (page refreshes the tree). */
  onMoved: () => void;
}

const MoveCommentModal = ({ intent, nodes, onClose, onMoved }: MoveCommentModalProps) => {
  const { t, i18n } = useTranslation(['taxonomy', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const formStyles = useFormStyles();
  const { reparentNode, updateNode } = useTaxonomyMutations();
  const isZh = i18n.language.startsWith('zh');

  const [comment, setComment] = useState('');
  const [showCommentError, setShowCommentError] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (intent) {
      setComment('');
      setShowCommentError(false);
    }
  }, [intent]);

  const summary = useMemo(() => {
    if (!intent) return '';
    const lang = isZh ? 'zh' : 'en';
    const node = nodes.find((n) => n.id === intent.nodeId);
    const nodeName = node ? (isZh ? node.nameZh : node.nameEn) : intent.nodeId;
    const parentLabel = intent.newParentId
      ? buildBreadcrumb(nodes, intent.newParentId, lang)
      : t('taxonomy:fields.topLevel');
    return intent.kind === 'reparent'
      ? t('taxonomy:move.reparentSummary', { name: nodeName, parent: parentLabel })
      : t('taxonomy:move.reorderSummary', { name: nodeName, parent: parentLabel });
  }, [intent, isZh, nodes, t]);

  const submit = async () => {
    if (!intent) return;
    if (!comment.trim()) {
      setShowCommentError(true);
      return;
    }
    setSaving(true);
    try {
      if (intent.kind === 'reparent') {
        await reparentNode(intent.nodeId, intent.newParentId, comment.trim());
      } else {
        await updateNode(intent.nodeId, {
          sortOrder: intent.newSortOrder,
          comment: comment.trim(),
        });
      }
      message.success(t('taxonomy:move.success'));
      onClose();
      onMoved();
    } catch (error) {
      const guard = extractGuardError(error);
      message.error(guard ? t(`taxonomy:errors.${guard}`) : t('common:messages.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={Boolean(intent)}
      title={t('taxonomy:move.title')}
      okText={t('taxonomy:move.action')}
      cancelText={t('common:actions.cancel')}
      onCancel={onClose}
      onOk={submit}
      confirmLoading={saving}
      width={480}
      okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      destroyOnHidden
    >
      <Text style={{ display: 'block', marginBottom: token.marginMD }}>{summary}</Text>

      <Text style={formStyles.labelStyle}>
        <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
        {t('taxonomy:fields.comment')}
      </Text>
      <Input.TextArea
        rows={3}
        value={comment}
        onChange={(event) => {
          setComment(event.target.value);
          if (event.target.value.trim()) setShowCommentError(false);
        }}
        placeholder={t('taxonomy:detail.commentPlaceholder')}
        style={{ ...formStyles.inputStyle, height: 'auto' }}
        status={showCommentError ? 'error' : undefined}
      />
      {showCommentError && (
        <Text style={formStyles.errorStyle}>{t('taxonomy:validation.commentRequired')}</Text>
      )}
    </Modal>
  );
};

export default MoveCommentModal;
